import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["nextBtn", "finalizeBtn", "creationMode"]
  static values = {
    step: String,
    complete: { type: Boolean, default: false }
  }

  connect() {
    console.log("[wizard-footer] connect: step =", this.stepValue)
    this.blockReason = null
    this.tooltip = null
    this.validate()
  }

  // Called on any input/change/click within the form
  validate() {
    const reason = this.firstBlockingReason()
    this.blockReason = reason
    const valid = !reason
    this.toggleNext(valid, reason)
  }

  firstBlockingReason() {
    switch (this.stepValue) {
      case "filters": return this.filtersBlockingReason()
      case "select": return this.selectBlockingReason()
      case "customize": return null
      default: return null
    }
  }

  filtersBlockingReason() {
    const name = this.element.querySelector("[name='autoboost[autoboost_name]']")
    if (!name?.value?.trim()) return "Autoboost name is required"

    const pageType = this.element.querySelector("[name='autoboost[page_type]']:checked")
    if (!pageType) return "Select a page type (Facebook or Instagram)"

    if (pageType.value === "facebook") {
      const pageId = this.element.querySelector("[name='autoboost[fb_page_id]']")
      if (!pageId?.value?.trim()) return "Select a Facebook page"
    } else if (pageType.value === "instagram") {
      const igId = this.element.querySelector("[name='autoboost[fb_instagram_account_id]']")
      if (!igId?.value?.trim()) return "Select an Instagram account"
    } else if (pageType.value === "tiktok") {
      const ttId = this.element.querySelector("[name='autoboost[tiktok_account_id]']")
      if (!ttId?.value?.trim()) return "Select a TikTok account"
    }

    return null
  }

  selectBlockingReason() {
    const fbAdAccount = this.element.querySelector("[name='autoboost[fb_ad_account_id]']")
    const ttAdAccount = this.element.querySelector("[name='autoboost[tiktok_ad_account_id]']")
    const adAccount = ttAdAccount || fbAdAccount
    if (!adAccount?.value?.trim()) return "Select an ad account"

    const mode = this.getCreationMode()
    if (mode === "created") {
      return this.createBlockingReason()
    }

    // Existing mode — Meta
    if (ttAdAccount?.value) {
      // TikTok existing mode
      const campaigns = this.element.querySelectorAll("[name='autoboost[tiktok_campaign_ids][]']:checked")
      if (!campaigns.length) return "Select at least one campaign"

      const adgroups = this.element.querySelectorAll("[name='autoboost[tiktok_adgroup_ids][]']:checked")
      if (!adgroups.length) return "Select at least one ad group"
    } else {
      // Meta existing mode
      const campaigns = this.element.querySelectorAll("[name='autoboost[campaign_ids][]']:checked")
      if (!campaigns.length) return "Select at least one campaign"

      const adsets = this.element.querySelectorAll("[name='autoboost[adset_ids][]']:checked")
      if (!adsets.length) return "Select at least one ad set"
    }

    return null
  }

  createBlockingReason() {
    const objective = this.element.querySelector("[name='autoboost[objective]']:checked")
    if (!objective?.value) return "Select a campaign objective"

    const budget = this.element.querySelector("[name='autoboost[daily_budget_cents]']")
    const budgetVal = parseInt(budget?.value || "0")
    if (budgetVal <= 0) return "Set a daily budget"

    // TikTok minimum budget check (€50/day)
    const ttAdAccount = this.element.querySelector("[name='autoboost[tiktok_ad_account_id]']")
    if (ttAdAccount?.value && budgetVal < 5000) return "TikTok requires a minimum budget of €50/day"

    const countriesInput = this.element.querySelector("[name='autoboost[targeting_countries]']")
    try {
      const countries = JSON.parse(countriesInput?.value || "[]")
      if (!countries.length) return "Add at least one target country"
    } catch {
      return "Add at least one target country"
    }

    const ageMin = parseInt(this.element.querySelector("[name='autoboost[targeting_age_min]']")?.value || "0")
    const ageMax = parseInt(this.element.querySelector("[name='autoboost[targeting_age_max]']")?.value || "0")
    if (ageMin < 18) return "Minimum age must be at least 18"
    if (ageMax < ageMin) return "Maximum age must be greater than minimum age"

    const objectiveVal = objective.value
    if (objectiveVal === "OUTCOME_SALES" || objectiveVal === "OUTCOME_LEADS") {
      const pixel = this.element.querySelector("[name='autoboost[conversion_pixel]']")
      if (!pixel?.value) return "Select a pixel for conversion tracking"
      const pixelBlocked = this.element.querySelector("[data-campaign-creator-target='pixelBlocked']")
      if (pixelBlocked?.value === "1") return "Selected pixel isn't accessible — choose another or request access"
    }

    return null
  }

  toggleNext(valid, reason) {
    if (!this.hasNextBtnTarget) return
    const btn = this.nextBtnTarget

    if (valid) {
      btn.disabled = false
      btn.classList.remove("opacity-40")
      btn.classList.remove("pointer-events-none")
      btn.removeAttribute("title")
      this.removeTooltipListeners(btn)
    } else {
      btn.disabled = false // keep clickable for reason display
      btn.classList.add("opacity-40")
      btn.classList.remove("pointer-events-none") // allow hover+click
      btn.setAttribute("title", reason || "")
      this.setupTooltipListeners(btn)
    }
  }

  setupTooltipListeners(btn) {
    if (btn._wizardBound) return
    btn._wizardBound = true

    btn._wizardEnter = () => this.showTooltip(btn)
    btn._wizardLeave = () => this.hideTooltip()
    btn._wizardClick = (e) => {
      if (this.blockReason) {
        e.preventDefault()
        e.stopPropagation()
        this.showReasonModal()
      }
    }

    btn.addEventListener("mouseenter", btn._wizardEnter)
    btn.addEventListener("mouseleave", btn._wizardLeave)
    btn.addEventListener("click", btn._wizardClick, { capture: true })
  }

  removeTooltipListeners(btn) {
    if (!btn._wizardBound) return
    btn.removeEventListener("mouseenter", btn._wizardEnter)
    btn.removeEventListener("mouseleave", btn._wizardLeave)
    btn.removeEventListener("click", btn._wizardClick, { capture: true })
    btn._wizardBound = false
    this.hideTooltip()
  }

  showTooltip(btn) {
    if (!this.blockReason) return
    this.hideTooltip()

    const tip = document.createElement("div")
    tip.className = "fixed z-50 px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg max-w-xs pointer-events-none"
    tip.textContent = this.blockReason
    document.body.appendChild(tip)

    const rect = btn.getBoundingClientRect()
    tip.style.left = `${rect.left + rect.width / 2 - tip.offsetWidth / 2}px`
    tip.style.top = `${rect.top - tip.offsetHeight - 8}px`

    this.tooltip = tip
  }

  hideTooltip() {
    if (this.tooltip) {
      this.tooltip.remove()
      this.tooltip = null
    }
  }

  showReasonModal() {
    const reason = this.blockReason
    if (!reason) return

    // Remove existing modal
    document.getElementById("wizard-reason-modal")?.remove()

    const modal = document.createElement("div")
    modal.id = "wizard-reason-modal"
    modal.className = "fixed inset-0 z-50 flex items-end justify-center pb-24"
    modal.innerHTML = `
      <div class="absolute inset-0" data-dismiss="true"></div>
      <div class="relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl px-6 py-4 max-w-sm w-full mx-4 animate-fade-in">
        <div class="flex items-start gap-3">
          <div class="shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-900 dark:text-white">Can't proceed yet</p>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">${reason}</p>
          </div>
          <button type="button" class="shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer" data-close="true">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>
    `

    document.body.appendChild(modal)

    const dismiss = () => { modal.remove(); this.hideTooltip() }
    modal.querySelector("[data-dismiss]").addEventListener("click", dismiss)
    modal.querySelector("[data-close]").addEventListener("click", dismiss)
    setTimeout(() => { document.addEventListener("keydown", function handler(e) { if (e.key === "Escape") { dismiss(); document.removeEventListener("keydown", handler) } }) }, 0)
  }

  // Mode toggle: switch between "Select existing" and "Create new"
  switchMode(event) {
    const mode = event.currentTarget.dataset.mode
    console.log("[wizard-footer] switchMode:", mode)

    // Update hidden input
    if (this.hasCreationModeTarget) {
      this.creationModeTarget.value = mode
    }

    // Toggle panel visibility
    const existingPanel = document.getElementById("mode-select-existing")
    const createPanel = document.getElementById("mode-create-new")

    if (mode === "created") {
      existingPanel?.classList.add("hidden")
      createPanel?.classList.remove("hidden")
    } else {
      existingPanel?.classList.remove("hidden")
      createPanel?.classList.add("hidden")
    }

    // Update toggle button styling
    const buttons = event.currentTarget.parentElement.querySelectorAll("button")
    buttons.forEach(btn => {
      if (btn.dataset.mode === mode) {
        btn.classList.add("bg-brand", "text-white")
        btn.classList.remove("text-gray-600")
      } else {
        btn.classList.remove("bg-brand", "text-white")
        btn.classList.add("text-gray-600")
      }
    })

    this.validate()
  }

  getCreationMode() {
    if (this.hasCreationModeTarget) {
      return this.creationModeTarget.value
    }
    return "existing"
  }

  // Re-validate when campaign-selector finishes loading (custom event)
  campaignsLoaded() { this.validate() }
  adsetsLoaded() { this.validate() }

  disconnect() {
    this.hideTooltip()
    document.getElementById("wizard-reason-modal")?.remove()
    console.log("[wizard-footer] disconnect")
  }
}
